import { useCallback, useEffect, useRef, useState } from 'react';
import { cancelResearchRun, createResearchRun, listResearchRuns } from '../atlasApi';

const ACTIVE_POLL_MS = 15000;

// Run-list state for the Live Web Research tool tab. Polls only while a run
// is active (live detail streaming is handled per-run by useLiveResearch).
export default function useResearchRuns({ enabled = true } = {}) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const list = await listResearchRuns({ limit: 50 });
      if (mountedRef.current) {
        setRuns(list);
        setError(null);
      }
      return list;
    } catch (e) {
      if (mountedRef.current) setError(e?.message || 'Failed to load research runs');
      return [];
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return undefined;
    setLoading(true);
    refresh();
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [enabled, refresh]);

  // Poll while any run is queued/running.
  useEffect(() => {
    const hasActive = runs.some((run) => run.status === 'queued' || run.status === 'running');
    if (hasActive && enabled) {
      if (!pollRef.current) {
        pollRef.current = setInterval(refresh, ACTIVE_POLL_MS);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return undefined;
  }, [runs, enabled, refresh]);

  const startRun = useCallback(
    async ({ objective, name, startUrl, useResidentialProxy }) => {
      setStarting(true);
      setStartError(null);
      try {
        const run = await createResearchRun({ objective, name, startUrl, useResidentialProxy });
        await refresh();
        return run;
      } catch (e) {
        setStartError(e?.message || 'Failed to start run');
        return null;
      } finally {
        if (mountedRef.current) setStarting(false);
      }
    },
    [refresh]
  );

  const cancelRun = useCallback(
    async (runId) => {
      try {
        await cancelResearchRun({ runId });
      } catch (e) {
        // Refresh below shows the true state either way.
      }
      refresh();
    },
    [refresh]
  );

  return { runs, loading, error, starting, startError, refresh, startRun, cancelRun };
}
